import React, { useEffect, useRef, useState } from 'react';
import { Send, Sparkles } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';

const SUGGESTIONS = [
    'What offers are available at Inorbit Cyberabad today?',
    'Suggest a birthday gift under ₹5000',
    'Where can I find Zara and Lacoste?',
    'Plan a weekend visit to Inorbit Malad for me',
    'How do I redeem my points for movies?',
];

function sessionId() {
    let s = localStorage.getItem('io_concierge_session');
    if (!s) { s = `sess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`; localStorage.setItem('io_concierge_session', s); }
    return s;
}

export default function Concierge() {
    const { user, type } = useAuth();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [busy, setBusy] = useState(false);
    const sid = useRef(sessionId()).current;
    const endRef = useRef(null);

    useEffect(() => {
        api.get(`/concierge/history/${sid}`).then(({ data }) => setMessages(data)).catch(() => {});
    }, [sid]);

    useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, busy]);

    const send = async (text) => {
        const msg = text || input;
        if (!msg.trim() || busy) return;
        setInput('');
        setMessages((m) => [...m, { role: 'user', content: msg, id: `local-${Date.now()}` }]);
        setBusy(true);
        try {
            const { data } = await api.post('/concierge/chat', {
                session_id: sid, message: msg,
                customer_id: type === 'customer' ? user?.id : null,
            });
            setMessages((m) => [...m, { role: 'assistant', content: data.reply, id: data.message_id }]);
        } catch (e) {
            setMessages((m) => [...m, { role: 'assistant', content: 'I apologise — connectivity blip. Please try again.', id: `err-${Date.now()}` }]);
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-ivory flex flex-col pt-16" data-testid="concierge-page">
            <div className="max-w-4xl mx-auto px-6 pt-10 w-full">
                <div className="flex items-center gap-2 mb-4">
                    <div className="overline">AI Concierge</div>
                    <span className="text-[10px] tracking-widest text-text-tertiary uppercase">• Powered by <span className="text-inorbit-orange font-semibold">Fundle.ai</span></span>
                </div>
                <h1 className="font-serif text-5xl text-navy leading-tight">Your personal <span className="italic text-gold">shopping companion.</span></h1>
                <p className="mt-4 text-text-secondary">Ask anything — offers, brands, gifts, itineraries, loyalty. Powered by AI, designed for Inorbit.</p>
            </div>

            <div className="flex-1 max-w-4xl mx-auto px-6 w-full mt-10">
                {messages.length === 0 && (
                    <div className="space-y-3" data-testid="concierge-suggestions">
                        <div className="overline">Try asking</div>
                        {SUGGESTIONS.map((s) => (
                            <button key={s} onClick={() => send(s)} className="block w-full text-left p-4 card-luxury text-sm text-navy hover:text-gold transition-colors">{s}</button>
                        ))}
                    </div>
                )}

                <div className="space-y-5 pb-32" data-testid="chat-messages">
                    {messages.map((m) => (
                        <div key={m.id} className={`flex gap-3 ${m.role === 'user' ? 'justify-end' : ''}`}>
                            {m.role === 'assistant' && (
                                <div className="w-10 h-10 bg-navy text-gold flex items-center justify-center shrink-0">
                                    <Sparkles size={16} strokeWidth={1.5} />
                                </div>
                            )}
                            <div className={`max-w-[80%] px-5 py-4 ${m.role === 'user' ? 'bg-navy text-white' : 'bg-white border border-border-subtle text-navy'}`}>
                                <div className="text-sm leading-relaxed whitespace-pre-wrap">{m.content}</div>
                            </div>
                        </div>
                    ))}
                    {busy && (
                        <div className="flex gap-3">
                            <div className="w-10 h-10 bg-navy text-gold flex items-center justify-center shrink-0"><Sparkles size={16} strokeWidth={1.5} /></div>
                            <div className="px-5 py-4 bg-white border border-border-subtle text-text-tertiary text-sm italic">Thinking…</div>
                        </div>
                    )}
                    <div ref={endRef} />
                </div>
            </div>

            <div className="fixed bottom-0 inset-x-0 bg-ivory border-t border-border-subtle z-20" data-testid="chat-input-bar">
                <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-3">
                    <input
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && send()}
                        placeholder="Ask the Inorbit One Concierge…"
                        className="flex-1 input-box"
                        disabled={busy}
                        data-testid="chat-input"
                    />
                    <button className="btn-primary" onClick={() => send()} disabled={!input.trim() || busy} data-testid="chat-send-btn">
                        <Send size={16} strokeWidth={1.5} />
                    </button>
                </div>
            </div>
        </div>
    );
}
